/**
 * The task board.
 *
 * A list panel with a few saved views, and a dialog for one task at a time.
 * Editing is field-at-a-time against the server rather than a form with a
 * Save button: two people often have the same task open, and a whole-form
 * save would have the slower one silently overwrite the other's changes.
 *
 * Everything here renders from the store, which the socket keeps current — so
 * a change someone else makes appears without a refresh.
 */

import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  isFinished,
  isSafeUrl,
  type Account,
  type Group,
  type Task,
  type TaskLink,
  type TaskPriority,
  type TaskStatus,
} from '@virtual-office/shared';

import * as api from '../api.js';
import type { Store } from '../state.js';
import { $, clear, el } from './dom.js';
import { toast } from './toasts.js';

/**
 * The saved views.
 *
 * `mine` first and default: the question people actually open a board to
 * answer is "what is on me", not "what exists".
 */
type View = 'mine' | 'open' | 'blocked' | 'done';

const VIEW_LABELS: Record<View, string> = {
  mine: 'Mine',
  open: 'All open',
  blocked: 'Blocked',
  done: 'Done',
};

/** Newest due date first among the undated, but dated work sorts to the top. */
function byUrgency(a: Task, b: Task): number {
  const weight: Record<TaskPriority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
  if (a.dueAt !== b.dueAt) {
    if (a.dueAt === null) return 1;
    if (b.dueAt === null) return -1;
    return a.dueAt - b.dueAt;
  }
  if (weight[a.priority] !== weight[b.priority]) return weight[a.priority] - weight[b.priority];
  return b.updatedAt - a.updatedAt;
}

/** "Fri 3 Oct", or "overdue" styling decided by the caller. */
const formatDue = (epochMs: number): string =>
  new Date(epochMs).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });

/** An <input type="date"> wants yyyy-mm-dd in *local* time, not an ISO instant. */
function toDateInput(epochMs: number | null): string {
  if (epochMs === null) return '';
  const date = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Due dates mean end of that day, so a task is not overdue at breakfast. */
function fromDateInput(value: string): number | null {
  if (!value) return null;
  const parts = value.split('-').map(Number);
  const [year, month, day] = parts;
  if (year === undefined || month === undefined || day === undefined) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
}

export class TasksPanel {
  private readonly panel = $('tasks');
  private readonly body = $('tasks-body');
  private readonly tabs = $('tasks-tabs');
  private readonly dialog = $('task-dialog');

  private view: View = 'mine';
  /** Task open in the dialog, or null. Kept as an id so pushes stay live. */
  private editing: string | null = null;

  /** Everyone in the office, for the assignee and approver pickers. */
  private accounts: Account[] = [];
  private groups: Group[] = [];
  private peopleLoaded = false;

  constructor(private readonly store: Store) {
    $('tasks-close').addEventListener('click', () => this.close());
    $('task-dialog-close').addEventListener('click', () => this.closeTask());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeTask();
    });
    // The global shortcut handler ignores every key while a modal is open, so
    // the dialog closes itself.
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.editing !== null) {
        event.stopPropagation();
        this.closeTask();
      }
    });

    this.store.on('tasks', () => {
      if (this.isOpen) this.render();
      // Rebuilding the dialog replaces its controls, which would throw away
      // half-typed text. Anyone else's edit can wait until the field is left;
      // your own only lands after a blur, by which point nothing has focus.
      if (this.editing !== null && !this.dialog.contains(document.activeElement)) {
        this.renderDialog();
      }
    });
  }

  get isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.panel.hidden = false;
    this.render();
    void this.load();
  }

  close(): void {
    this.panel.hidden = true;
    this.closeTask();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  /**
   * Fetch the board, and the roster the pickers need.
   *
   * Only once per session for the people: names change rarely, and re-reading
   * the whole office every time the panel opens is a cost for nothing.
   */
  private async load(): Promise<void> {
    try {
      const { tasks, categories } = await api.listTasks();
      this.store.setCategories(categories);
      this.store.setTasks(tasks);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not load tasks.', 'error');
    }
    if (this.peopleLoaded) return;
    try {
      const { groups, accounts } = await api.listGroups();
      this.groups = groups.filter((group) => group.ownerId === null && group.kind !== 'tag');
      this.accounts = [...accounts].sort((a, b) => a.name.localeCompare(b.name));
      this.peopleLoaded = true;
      if (this.editing !== null) this.renderDialog();
    } catch {
      // Not fatal: the board still reads, the pickers just stay empty.
    }
  }

  /* --- list ---------------------------------------------------------------- */

  private visible(): Task[] {
    const me = this.store.meId;
    const all = [...this.store.tasks.values()];
    const open = all.filter((task) => !isFinished(task.status));

    switch (this.view) {
      case 'mine':
        // Anything wanting something from you: yours to do, or yours to sign off.
        return open
          .filter((task) => task.assigneeId === me || task.approverId === me)
          .sort(byUrgency);
      case 'blocked':
        return open.filter((task) => task.blockedBy.length > 0).sort(byUrgency);
      case 'done':
        return all.filter((task) => isFinished(task.status)).sort((a, b) => b.updatedAt - a.updatedAt);
      case 'open':
      default:
        return open.sort(byUrgency);
    }
  }

  private render(): void {
    clear(this.tabs);
    for (const view of ['mine', 'open', 'blocked', 'done'] as View[]) {
      const tab = el('button', { className: 'board-tab', text: VIEW_LABELS[view] });
      tab.type = 'button';
      if (this.view === view) tab.classList.add('selected');
      tab.addEventListener('click', () => {
        this.view = view;
        this.render();
      });
      this.tabs.append(tab);
    }

    clear(this.body);
    const tasks = this.visible();
    if (tasks.length === 0) {
      this.body.append(
        el('p', {
          className: 'empty',
          text: this.store.tasksLoaded ? 'Nothing here.' : 'Loading…',
        }),
      );
      return;
    }
    for (const task of tasks) this.body.append(this.renderCard(task));
  }

  private renderCard(task: Task): HTMLElement {
    const card = el('button', { className: 'task-card' });
    card.type = 'button';
    if (task.blockedBy.length > 0) card.classList.add('is-blocked');
    if (isFinished(task.status)) card.classList.add('is-done');

    const head = el('div', { className: 'task-card-head' }, [
      el('span', { className: 'task-number', text: `#${task.number}` }),
      el('span', { className: 'task-title', text: task.title }),
      el('span', {
        className: `task-status status-${task.status}`,
        text: TASK_STATUS_LABELS[task.status],
      }),
    ]);
    card.append(head);

    const bits: string[] = [];
    if (task.assigneeName) bits.push(task.assigneeName);
    else bits.push('Unassigned');
    if (task.categoryName) bits.push(task.categoryName);
    if (task.priority !== 'normal') bits.push(TASK_PRIORITY_LABELS[task.priority]);
    if (task.approverId && task.approvedAt === null) bits.push(`${task.approverName} to approve`);

    const meta = el('div', { className: 'task-card-meta', text: bits.join(' · ') });
    card.append(meta);

    if (task.dueAt !== null) {
      const overdue = task.dueAt < Date.now() && !isFinished(task.status);
      const due = el('span', {
        className: overdue ? 'task-due overdue' : 'task-due',
        text: overdue ? `Overdue — ${formatDue(task.dueAt)}` : `Due ${formatDue(task.dueAt)}`,
      });
      meta.append(document.createTextNode(' '), due);
    }

    // The blockers are the whole reason to record a dependency, so name them
    // on the card rather than making someone open the task to find out.
    if (task.blockedBy.length > 0) {
      card.append(
        el('div', {
          className: 'task-blocked',
          text: `Waiting on ${task.blockedBy.map((b) => `#${b.number} ${b.title}`).join(', ')}`,
        }),
      );
    }

    card.addEventListener('click', () => this.openTask(task.id));
    return card;
  }

  /* --- creating ------------------------------------------------------------ */

  /** A new task needs only a title; everything else is set in the dialog. */
  async createFromPrompt(): Promise<void> {
    const title = prompt('What needs doing?');
    if (title === null) return;
    if (title.trim() === '') return;
    try {
      const { task } = await api.createTask({ title: title.trim(), assigneeId: this.store.meId });
      this.store.putTask(task);
      this.openTask(task.id);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not create that.', 'error');
    }
  }

  /* --- one task ------------------------------------------------------------ */

  /**
   * Open the board on one task, from outside — following a notification.
   *
   * The task may not be in the store yet if the board has never been opened
   * this session, so the fetch comes first and the dialog after.
   */
  openById(id: string): void {
    this.panel.hidden = false;
    this.render();
    if (this.store.tasks.has(id)) {
      this.openTask(id);
      void this.load();
      return;
    }
    void (async () => {
      await this.load();
      if (this.store.tasks.has(id)) this.openTask(id);
      else toast('That task is gone.', 'error');
    })();
  }

  private openTask(id: string): void {
    this.editing = id;
    this.dialog.hidden = false;
    this.renderDialog();
    if (!this.peopleLoaded) void this.load();
  }

  private closeTask(): void {
    this.editing = null;
    this.dialog.hidden = true;
  }

  /** Send one field and let the push update the store. */
  private async patch(id: string, draft: api.TaskDraft): Promise<void> {
    try {
      const { task } = await api.updateTask(id, draft);
      this.store.putTask(task);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save that.', 'error');
      // Re-render from the store so the control snaps back to the real value.
      this.renderDialog();
    }
  }

  private renderDialog(): void {
    const task = this.editing === null ? undefined : this.store.tasks.get(this.editing);
    if (!task) return this.closeTask();
    const id = task.id;

    $('task-dialog-title').textContent = `#${task.number}`;
    const body = $('task-dialog-body');
    clear(body);

    /* Title. Committed on blur and on Enter, not on every keystroke. */
    const title = el('input', { className: 'task-title-input' });
    title.value = task.title;
    title.maxLength = 200;
    const commitTitle = () => {
      const value = title.value.trim();
      if (value === '' || value === task.title) {
        title.value = task.title;
        return;
      }
      void this.patch(id, { title: value });
    };
    title.addEventListener('blur', commitTitle);
    title.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') title.blur();
    });
    body.append(title);

    /* Status, priority, category — the row of quick controls. */
    const row = el('div', { className: 'task-controls' });

    row.append(
      this.select(
        'Status',
        TASK_STATUSES.map((value) => ({ value, label: TASK_STATUS_LABELS[value] })),
        task.status,
        (value) => void this.patch(id, { status: value as TaskStatus }),
      ),
      this.select(
        'Priority',
        TASK_PRIORITIES.map((value) => ({ value, label: TASK_PRIORITY_LABELS[value] })),
        task.priority,
        (value) => void this.patch(id, { priority: value as TaskPriority }),
      ),
    );

    // Only live categories are offered, but a task already on a retired one
    // keeps showing it — otherwise the control would silently misreport the
    // task as uncategorised.
    const categories = this.store.categories.filter(
      (category) => !category.archived || category.id === task.categoryId,
    );
    row.append(
      this.select(
        'Category',
        [
          { value: '', label: categories.length === 0 ? 'None set up yet' : 'None' },
          ...categories.map((category) => ({ value: category.id, label: category.name })),
        ],
        task.categoryId ?? '',
        (value) => void this.patch(id, { categoryId: value || null }),
      ),
    );

    const due = el('input');
    due.type = 'date';
    due.value = toDateInput(task.dueAt);
    due.addEventListener('change', () => void this.patch(id, { dueAt: fromDateInput(due.value) }));
    row.append(this.field('Due', due));

    body.append(row);

    /* People. */
    const people = el('div', { className: 'task-controls' });
    const roster = [
      { value: '', label: 'Nobody' },
      ...this.accounts.map((account) => ({ value: account.id, label: account.name })),
    ];
    people.append(
      this.select('Assignee', roster, task.assigneeId ?? '', (value) =>
        void this.patch(id, { assigneeId: value || null }),
      ),
      this.select('Approver', roster, task.approverId ?? '', (value) =>
        void this.patch(id, { approverId: value || null }),
      ),
      this.select(
        'Team',
        [
          { value: '', label: 'None' },
          ...this.groups.map((group) => ({ value: group.id, label: group.name })),
        ],
        task.groupId ?? '',
        (value) => void this.patch(id, { groupId: value || null }),
      ),
    );
    body.append(people);

    body.append(
      el('p', {
        className: 'hint-inline',
        text: task.assignerName
          ? `Raised by ${task.assignerName}${
              task.approvedAt === null ? '' : ` · approved by ${task.approverName}`
            }`
          : 'Raised by somebody who has since left.',
      }),
    );

    /* Description. */
    const description = el('textarea');
    description.rows = 6;
    description.value = task.description;
    description.maxLength = 20_000;
    description.placeholder = 'What does done look like?';
    description.addEventListener('blur', () => {
      if (description.value !== task.description) {
        void this.patch(id, { description: description.value });
      }
    });
    body.append(this.field('Details', description));

    body.append(this.renderLinks(task));
    body.append(this.renderDependencies(task));
    body.append(this.renderActions(task));
  }

  /* --- dialog pieces -------------------------------------------------------- */

  private field(label: string, control: HTMLElement): HTMLElement {
    return el('label', { className: 'field' }, [el('span', { text: label }), control]);
  }

  private select(
    label: string,
    options: Array<{ value: string; label: string }>,
    current: string,
    onChange: (value: string) => void,
  ): HTMLElement {
    const select = el('select');
    for (const option of options) {
      const node = el('option', { text: option.label });
      node.value = option.value;
      if (option.value === current) node.selected = true;
      select.append(node);
    }
    select.addEventListener('change', () => onChange(select.value));
    return this.field(label, select);
  }

  private renderLinks(task: Task): HTMLElement {
    const section = el('div', { className: 'task-section' }, [
      el('p', { className: 'option-label', text: 'Links' }),
    ]);

    for (const link of task.links) {
      const row = el('div', { className: 'task-link-row' });
      // Only ever http(s) — the server refuses the rest, and this is the
      // second gate in case an older row predates that check.
      if (isSafeUrl(link.url)) {
        const anchor = el('a', { className: 'task-link', text: link.label });
        anchor.href = link.url;
        anchor.target = '_blank';
        // noopener so the opened page cannot reach back through window.opener.
        anchor.rel = 'noopener noreferrer';
        row.append(anchor);
      } else {
        row.append(el('span', { className: 'task-link', text: link.label }));
      }

      const remove = el('button', { className: 'ghost small', text: '✕' });
      remove.type = 'button';
      remove.title = 'Remove link';
      remove.addEventListener('click', () => {
        const links = task.links.filter((other) => other.url !== link.url);
        void this.patch(task.id, { links });
      });
      row.append(remove);
      section.append(row);
    }

    const url = el('input');
    url.placeholder = 'https://…';
    const label = el('input');
    label.placeholder = 'Label (optional)';
    const add = el('button', { text: 'Add' });
    add.type = 'button';
    add.addEventListener('click', () => {
      const value = url.value.trim();
      if (!isSafeUrl(value)) {
        toast('Links must start with http:// or https://', 'error');
        return;
      }
      const links: TaskLink[] = [...task.links, { label: label.value.trim() || value, url: value }];
      url.value = '';
      label.value = '';
      void this.patch(task.id, { links });
    });
    section.append(el('div', { className: 'task-link-add' }, [url, label, add]));
    return section;
  }

  private renderDependencies(task: Task): HTMLElement {
    const section = el('div', { className: 'task-section' }, [
      el('p', { className: 'option-label', text: 'Waits for' }),
    ]);

    if (task.dependsOn.length === 0) {
      section.append(el('p', { className: 'hint-inline', text: 'Nothing. Good.' }));
    }

    for (const dependencyId of task.dependsOn) {
      const other = this.store.tasks.get(dependencyId);
      const blocking = task.blockedBy.some((b) => b.id === dependencyId);
      const row = el('div', { className: blocking ? 'task-link-row is-blocked' : 'task-link-row' });
      row.append(
        el('span', {
          className: 'task-link',
          text: other
            ? `#${other.number} ${other.title}${blocking ? '' : ' ✓'}`
            : 'A task you cannot see',
        }),
      );
      const remove = el('button', { className: 'ghost small', text: '✕' });
      remove.type = 'button';
      remove.title = 'Stop waiting for this';
      remove.addEventListener('click', () => {
        void (async () => {
          try {
            await api.setDependency(task.id, dependencyId, true);
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not change that.', 'error');
          }
        })();
      });
      row.append(remove);
      section.append(row);
    }

    // Candidates: any other unfinished task not already depended on. The
    // server still refuses anything that would close a loop; this only keeps
    // the obvious nonsense out of the list.
    const candidates = [...this.store.tasks.values()]
      .filter(
        (other) =>
          other.id !== task.id && !isFinished(other.status) && !task.dependsOn.includes(other.id),
      )
      .sort((a, b) => a.number - b.number);

    const picker = el('select');
    const blank = el('option', { text: 'Add something it waits for…' });
    blank.value = '';
    picker.append(blank);
    for (const other of candidates) {
      const option = el('option', { text: `#${other.number} ${other.title}` });
      option.value = other.id;
      picker.append(option);
    }
    picker.addEventListener('change', () => {
      const dependsOn = picker.value;
      picker.value = '';
      if (!dependsOn) return;
      void (async () => {
        try {
          await api.setDependency(task.id, dependsOn);
        } catch (error) {
          toast(error instanceof Error ? error.message : 'Could not add that.', 'error');
        }
      })();
    });
    section.append(picker);
    return section;
  }

  private renderActions(task: Task): HTMLElement {
    const row = el('div', { className: 'row task-actions' });
    const me = this.store.meId;

    // Approval is the approver's alone, so the button only exists for them.
    if (task.approverId === me && task.approvedAt === null) {
      const approve = el('button', { text: 'Approve' });
      approve.type = 'button';
      approve.addEventListener('click', () => {
        void (async () => {
          try {
            const { task: updated } = await api.approveTask(task.id);
            this.store.putTask(updated);
            toast(`#${updated.number} approved.`);
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not approve.', 'error');
          }
        })();
      });
      row.append(approve);
    }

    const canDelete = this.store.me?.role === 'admin' || task.assignerId === me;
    if (canDelete) {
      const remove = el('button', { className: 'ghost small danger', text: 'Delete' });
      remove.type = 'button';
      remove.addEventListener('click', () => {
        void (async () => {
          if (!confirm(`Delete #${task.number} "${task.title}"? Cancelling keeps the history.`)) {
            return;
          }
          try {
            await api.deleteTask(task.id);
            this.store.removeTask(task.id);
            this.closeTask();
          } catch (error) {
            toast(error instanceof Error ? error.message : 'Could not delete.', 'error');
          }
        })();
      });
      row.append(remove);
    }
    return row;
  }
}
